import { Fragment, useId, useRef, useState } from "react";
import {
  ALL_SUBJECTS,
  REFERRAL_NOTE_MAX,
  SUBJECT_TITLES,
  checkReferralName,
  checkReferralNote,
  normalizeReferralContact,
  type ReferralContactKind,
  type Subject,
} from "@edu/shared";
import { ApiError, createColleagueReferral } from "../../lib/api.ts";
import { SegmentedRadio } from "../../lib/SegmentedRadio.tsx";
import { Dialog } from "../shell/Dialog.tsx";
import {
  CONTACT_KINDS,
  CONTACT_LABEL,
  CONTACT_PLACEHOLDER,
  TELEGRAM_LINK_HINT,
  contactProblemText,
  nameProblemText,
  noteLeftText,
  noteProblemText,
  sendErrorText,
} from "./referral-text.ts";

/**
 * «Позвать коллегу»: преподаватель оставляет имя и контакт коллеги, заявка
 * уезжает владельцу в панель (`docs/colleague-referral-design.md`, раздел 6).
 *
 * Окном, а не адресом, по той же причине, что «Мои предметы»: ссылкой на форму
 * никто никогда не поделится, а «назад» возвращал бы в полузаполненную форму.
 *
 * ⚠️ **Черновик формы нигде не сохраняется**: ни `localStorage`, ни
 * `sessionStorage`, ни состояние выше окна. Всё введённое живёт в состоянии
 * ЭТОГО компонента и пропадает вместе с окном. Имя и контакт коллеги это ПДн
 * третьего лица, и на чужом или общем компьютере они не должны пережить окно.
 * По той же причине у трёх полей `autoComplete="off"`: браузер не должен
 * запомнить чужой телефон и подсказать его потом в другой форме.
 *
 * ⚠️ **Проверки те же, что у сервера**, из `@edu/shared`, а не свои: подсказка
 * под полем и отказ схемы обязаны совпадать, иначе контакт проходит форму и
 * падает на сервере. Имя и контакт уезжают сырыми, нормализует сервер.
 *
 * ⚠️ **`maxLength` у полей нет намеренно**, как у заметки о неточности: он молча
 * обрезал бы вставленный текст, и уехало бы не то, что человек видел. Текст
 * уезжает целиком или не уезжает, перебор длины называет подсказка `too_long`.
 *
 * ⚠️ **Под каждым полем ОДИН слот** (правило блока «Подпись, подсказка,
 * ошибка» в `styles.css`): ошибка ЗАМЕНЯЕТ подсказку, а не встаёт под ней,
 * иначе форма прыгает в момент нажатия «Отправить». `aria-describedby` идёт по
 * тому же выбору.
 *
 * Списка своих заявок и их судьбы здесь нет (решение 2 проекта): после отправки
 * только подтверждение.
 */
export function ColleagueReferralDialog(props: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<ReferralContactKind>("telegram");
  const [contact, setContact] = useState("");
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [note, setNote] = useState("");
  /**
   * Где подсказку уже можно показывать: после ухода из поля или после нажатия
   * «Отправить». Раньше нельзя: «напишите, как зовут» под полем, в которое
   * ещё не успели встать, читается упрёком. Перебор длины виден сразу (`shown`).
   */
  const [visited, setVisited] = useState<Record<Field, boolean>>(NOT_VISITED);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const nameRef = useRef<HTMLInputElement>(null);
  const contactRef = useRef<HTMLInputElement>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);

  /*
    Проверки считаются на каждом рендере из текущих значений, а не хранятся.
    Поэтому переключённый тип пересчитывает подсказку под контактом сразу: ник,
    проверенный как телефон, не держит старую ошибку.
  */
  const nameCheck = checkReferralName(name);
  const contactCheck = normalizeReferralContact(kind, contact);
  const noteCheck = checkReferralNote(note);
  const shown = (field: Field, problem: string): boolean =>
    visited[field] || tried || problem === "too_long";

  const nameError =
    !nameCheck.ok && shown("name", nameCheck.problem) ? nameProblemText(nameCheck.problem) : null;
  const contactError =
    !contactCheck.ok && shown("contact", contactCheck.problem)
      ? contactProblemText(contactCheck.problem)
      : null;
  const noteError =
    !noteCheck.ok && shown("note", noteCheck.problem) ? noteProblemText(noteCheck.problem) : null;
  const visit = (field: Field): void => setVisited((was) => ({ ...was, [field]: true }));

  /**
   * ⚠️ Пока идёт запрос, окно не закрывается ни крестиком, ни Esc, ни щелчком
   * мимо: закрытое посреди запроса окно оставило бы человека без ответа, ушла
   * заявка или нет, а повтор занял бы второе место из пяти.
   */
  const close = busy ? () => undefined : props.onClose;

  const reset = (): void => {
    setName("");
    setKind("telegram");
    setContact("");
    setSubjects([]);
    setNote("");
    setVisited(NOT_VISITED);
    setTried(false);
    setErr(null);
    setDone(false);
  };

  /** Порядок держит `ALL_SUBJECTS`, как в «Моих предметах». */
  const toggle = (subject: Subject, on: boolean): void => {
    setSubjects((was) => ALL_SUBJECTS.filter((s) => (s === subject ? on : was.includes(s))));
  };

  const submit = (): void => {
    if (busy) return;
    setTried(true);
    /*
      Фокус уходит в первое поле с подсказкой: на телефоне оно может стоять
      выше видимой части окна, и без этого нажатие «Отправить» выглядело бы
      несработавшим.
    */
    if (!nameCheck.ok) {
      nameRef.current?.focus();
      return;
    }
    if (!contactCheck.ok) {
      contactRef.current?.focus();
      return;
    }
    if (!noteCheck.ok) {
      noteRef.current?.focus();
      return;
    }
    void (async () => {
      setBusy(true);
      setErr(null);
      try {
        await createColleagueReferral({
          colleagueName: name,
          contactKind: kind,
          contact,
          subjects,
          // Пустой комментарий не уезжает вовсе: сервер прочёл бы его тем же `null`
          ...(noteCheck.value === null ? {} : { note: noteCheck.value }),
        });
        setDone(true);
      } catch (e) {
        /*
          ⚠️ Форма остаётся заполненной: переписывать имя и контакт после обрыва
          связи никто не станет. Нет `ApiError` значит ответа не было вовсе.
        */
        setErr(sendErrorText(e instanceof ApiError ? e.code : null));
      } finally {
        setBusy(false);
      }
    })();
  };

  /*
    ⚠️ Окно ОДНО на всё время жизни, а подтверждение и форма это его дети:
    второй `<Dialog>` на успехе закрыл бы нативное окно и открыл новое, то есть
    мигание и лишний круг фокуса. Тела и подвалы разного типа, поэтому кнопки и
    поля и так монтируются заново, и `autoFocus` («Готово» после успеха, имя
    после «Позвать ещё коллегу») срабатывает; ключи это страховка на случай,
    если типы однажды совпадут. Ключа у самого `Dialog` нет.
  */
  return (
    <Dialog
      title="Позвать коллегу"
      onClose={close}
      onSubmit={
        done
          ? undefined
          : (e) => {
              e.preventDefault();
              submit();
            }
      }
      foot={
        done ? (
          <SentFoot key="sent" onAgain={reset} onClose={props.onClose} />
        ) : (
          <Fragment key="form">
            <button
              type="button"
              className="btn btn--quiet"
              onClick={props.onClose}
              disabled={busy}
            >
              Отмена
            </button>
            <button type="submit" className="btn btn--primary" disabled={busy} aria-busy={busy}>
              {busy ? "Отправляем…" : "Отправить"}
            </button>
          </Fragment>
        )
      }
    >
      {done ? (
        <ReferralSent key="sent" />
      ) : (
        <div className="form" key="form">
          <p className="hint">
            Сначала спросите коллегу, можно ли дать нам контакт. Если сможем подключить коллегу,
            напишем сами, скажем, что контакт от вас, и пришлём ссылку, по которой можно завести
            кабинет.
          </p>

          <ContactFields
            name={name}
            onName={setName}
            nameError={nameError}
            nameRef={nameRef}
            kind={kind}
            onKind={setKind}
            contact={contact}
            onContact={setContact}
            contactError={contactError}
            contactRef={contactRef}
            onVisit={visit}
          />

          <SubjectChecks chosen={subjects} onToggle={toggle} />

          <NoteField
            note={note}
            onNote={setNote}
            error={noteError}
            noteRef={noteRef}
            onVisit={() => visit("note")}
          />

          {err !== null && <p className="err">{err}</p>}
        </div>
      )}
    </Dialog>
  );
}

type Field = "name" | "contact" | "note";

const NOT_VISITED: Record<Field, boolean> = { name: false, contact: false, note: false };

/**
 * Кто и как с ним связаться: имя, тип контакта и сам контакт.
 *
 * Подпись обнимает поле, а слот под ним стоит СЕСТРОЙ подписи, а не внутри неё:
 * текст внутри `<label>` уезжает в имя поля, и читалка читала бы ошибку как
 * часть подписи. К полю слот привязывает `aria-describedby`.
 */
function ContactFields(props: {
  name: string;
  onName: (value: string) => void;
  nameError: string | null;
  nameRef: React.RefObject<HTMLInputElement | null>;
  kind: ReferralContactKind;
  onKind: (kind: ReferralContactKind) => void;
  contact: string;
  onContact: (value: string) => void;
  contactError: string | null;
  contactRef: React.RefObject<HTMLInputElement | null>;
  onVisit: (field: "name" | "contact") => void;
}) {
  const nameSlotId = useId();
  const contactSlotId = useId();
  /** Строка про ссылку t.me только у Telegram и только пока нет ошибки. */
  const contactSlot =
    props.contactError ?? (props.kind === "telegram" ? TELEGRAM_LINK_HINT : null);

  return (
    <>
      <label className="field">
        <span className="lbl">Как зовут коллегу</span>
        <input
          ref={props.nameRef}
          className="fld"
          value={props.name}
          onChange={(e) => props.onName(e.target.value)}
          onBlur={() => props.onVisit("name")}
          placeholder="Например: Ирина Петровна"
          autoComplete="off"
          aria-invalid={props.nameError !== null}
          aria-describedby={props.nameError === null ? undefined : nameSlotId}
          autoFocus
        />
      </label>
      {props.nameError !== null && (
        <p className="hint hint--err" id={nameSlotId}>
          {props.nameError}
        </p>
      )}

      {/*
        Имя группе даёт `ariaLabel` сегмента, поэтому видимая подпись над ним
        для читалки погашена: иначе «Как связаться» прозвучало бы дважды.
      */}
      <div className="field">
        <span className="lbl" aria-hidden="true">
          Как связаться
        </span>
        <SegmentedRadio
          ariaLabel="Как связаться"
          segments={CONTACT_KINDS}
          value={props.kind}
          onPick={(id) => {
            const picked = CONTACT_KINDS.find((k) => k.id === id);
            if (picked !== undefined) props.onKind(picked.id);
          }}
        />
      </div>

      <label className="field">
        <span className="lbl">{CONTACT_LABEL[props.kind]}</span>
        <input
          ref={props.contactRef}
          className="fld"
          value={props.contact}
          onChange={(e) => props.onContact(e.target.value)}
          onBlur={() => props.onVisit("contact")}
          placeholder={CONTACT_PLACEHOLDER[props.kind]}
          /*
            ⚠️ Клавиатуру на телефоне выбирает `inputMode`, а не `type`. Поле
            `type="email"` внутри настоящей `<form>` проверяет браузер сам и
            останавливает отправку своим пузырём с английским текстом, мимо
            наших подсказок; проверку держит `normalizeReferralContact`, и
            она одна на форму и сервер.
          */
          type="text"
          inputMode={props.kind === "email" ? "email" : props.kind === "phone" ? "tel" : "text"}
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          aria-invalid={props.contactError !== null}
          aria-describedby={contactSlot === null ? undefined : contactSlotId}
        />
      </label>
      {contactSlot !== null && (
        <p className={props.contactError === null ? "hint" : "hint hint--err"} id={contactSlotId}>
          {contactSlot}
        </p>
      )}
    </>
  );
}

/** Комментарий: в слоте под полем ошибка, иначе счётчик у края, иначе ничего. */
function NoteField(props: {
  note: string;
  onNote: (value: string) => void;
  error: string | null;
  noteRef: React.RefObject<HTMLTextAreaElement | null>;
  onVisit: () => void;
}) {
  const slotId = useId();
  /* Счётчик только у края: обычный комментарий это одна строка */
  const slot = props.error ?? noteLeftText(REFERRAL_NOTE_MAX - props.note.trim().length);

  return (
    <>
      <label className="field">
        <span className="lbl">Комментарий, если нужен</span>
        <textarea
          ref={props.noteRef}
          className="fld"
          value={props.note}
          onChange={(e) => props.onNote(e.target.value)}
          onBlur={props.onVisit}
          placeholder="Например: готовит к ОГЭ, удобнее писать вечером"
          rows={3}
          autoComplete="off"
          aria-invalid={props.error !== null}
          aria-describedby={slot === null ? undefined : slotId}
        />
      </label>
      {slot !== null && (
        <p
          className={props.error === null ? "hint" : "hint hint--err"}
          id={slotId}
          aria-live="polite"
        >
          {slot}
        </p>
      )}
    </>
  );
}

/** Подтверждение после отправки: тело окна. Подвал у него свой, `SentFoot`. */
function ReferralSent() {
  return (
    <div className="form">
      <p className="form__done">
        Спасибо, контакт у нас. Если сможем подключить коллегу, напишем сами.
      </p>
    </div>
  );
}

/** Подвал подтверждения: «Позвать ещё коллегу» даёт пустую форму. */
function SentFoot(props: { onAgain: () => void; onClose: () => void }) {
  return (
    <>
      <button type="button" className="btn btn--quiet" onClick={props.onAgain}>
        Позвать ещё коллегу
      </button>
      <button type="button" className="btn btn--primary" onClick={props.onClose} autoFocus>
        Готово
      </button>
    </>
  );
}

/** Предметы коллеги галочками, в порядке `ALL_SUBJECTS`; может быть пусто. */
function SubjectChecks(props: {
  chosen: readonly Subject[];
  onToggle: (subject: Subject, on: boolean) => void;
}) {
  return (
    <fieldset className="field">
      <legend className="lbl">Какие предметы ведёт, если знаете</legend>
      {ALL_SUBJECTS.map((subject) => (
        <label key={subject} className="form__check">
          <input
            type="checkbox"
            checked={props.chosen.includes(subject)}
            onChange={(e) => props.onToggle(subject, e.target.checked)}
          />
          {SUBJECT_TITLES[subject]}
        </label>
      ))}
    </fieldset>
  );
}
